import { Button, CheckboxInput, SubmitButton, TextInput } from "@ameelio/ui";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import { Box, Grid, Stack, Typography } from "@mui/material";
import { sub } from "date-fns";
import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import DateInput from "../lib/DateInput";
import {
  isDateOfBirth,
  isPhoneNumber,
  isRequired,
  isRequiredBoolean,
  MAX_PHONE_NUMBER_LENGTH,
  MAX_USER_AGE,
  mergeRules,
} from "../lib/validate";
import { TermsPrivacySignUp } from "./TermsPrivacy";

export type ProfileStepData = {
  firstName: string;
  lastName?: string;
  phone: string;
  dateOfBirth?: Date;
  termsOfUse: boolean;
};

type Props = {
  defaultValues: ProfileStepData;
  onBack: () => void;
  onSubmit: (data: ProfileStepData) => void | Promise<void>;
};

// TODO: terms URL
// TODO: privacy policy URL
export default function ProfileForm({
  defaultValues,
  onBack,
  onSubmit,
}: Props) {
  const { t } = useTranslation();

  const {
    control,
    handleSubmit,
    formState: { isSubmitting },
  } = useForm<ProfileStepData>({
    mode: "onTouched",
    defaultValues,
  });

  return (
    <form
      onSubmit={handleSubmit((data) =>
        onSubmit({
          firstName: data.firstName,
          lastName: data.lastName,
          phone: data.phone,
          dateOfBirth: data.dateOfBirth,
          termsOfUse: data.termsOfUse,
        })
      )}
    >
      <Box
        sx={{
          marginTop: 2,
          marginBottom: 6,
        }}
      >
        <Typography variant="body1">
          {t(
            "Finally, please enter your name and date of birth. Security officials will use this later to verify your identity, so be sure it matches your legal ID."
          )}
        </Typography>
      </Box>
      <Stack
        spacing={2}
        sx={{
          marginBottom: 6,
        }}
      >
        <TextInput
          control={control}
          name="firstName"
          autoComplete="given-name"
          label={t("First name (legal)")}
          rules={isRequired(t("Please enter your first name."))}
        />
        <TextInput
          control={control}
          name="lastName"
          autoComplete="family-name"
          label={t("Last name (legal)")}
        />
        <TextInput
          control={control}
          name="phone"
          type="tel"
          autoComplete="tel"
          label={t("Phone number")}
          slotProps={{ htmlInput: { maxLength: MAX_PHONE_NUMBER_LENGTH } }}
          rules={mergeRules(
            isRequired(t("Please enter your phone number.")),
            isPhoneNumber()
          )}
        />
        <DateInput
          control={control}
          name="dateOfBirth"
          label={t("Date of birth")}
          autoComplete="bday"
          disableFuture
          disableHighlightToday
          minDate={sub(new Date(), { years: MAX_USER_AGE, days: 364 })}
          rules={isDateOfBirth()}
        />
      </Stack>
      <Box
        sx={{
          marginBottom: 6,
        }}
      >
        <CheckboxInput
          control={control}
          name="termsOfUse"
          label={<TermsPrivacySignUp />}
          rules={isRequiredBoolean(t("Please accept the terms of use."))}
        />
      </Box>

      <Grid
        container
        sx={{
          justifyContent: "space-between",
        }}
      >
        <Button onClick={onBack}>
          <ArrowBackIcon />
          &nbsp;
          {t("Back")}
        </Button>
        <SubmitButton submitting={isSubmitting}>{t("Finish")}</SubmitButton>
      </Grid>
    </form>
  );
}
